import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  CheckCircle2, 
  Shield, 
  Zap, 
  FileText, 
  Send, 
  BarChart3,
  RefreshCw,
  MessageSquare,
  Edit3,
  List,
  ChevronDown,
  Menu,
  X,
  Play,
  Star,
  Check,
  AlertCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';
import '../styles/Home.css';

const Home = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [openFaq, setOpenFaq] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1);
      if (hash) {
        const element = document.getElementById(hash);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const features = [
    {
      icon: <CheckCircle2 className="feature-icon-svg" />,
      title: "Smart Phone Validation",
      description: "Automatic phone number validation with country code detection and format correction to ensure delivery success."
    },
    {
      icon: <MessageSquare className="feature-icon-svg" />,
      title: "Meta Template Mode",
      description: "Use pre-approved WhatsApp Business templates for marketing, authentication, and utility messages with guaranteed delivery."
    },
    {
      icon: <Edit3 className="feature-icon-svg" />,
      title: "Custom Template Editor",
      description: "Create personalized messages using {ColumnName} placeholders that automatically pull data from your sheet columns."
    },
    {
      icon: <List className="feature-icon-svg" />,
      title: "Bulk Send from Selected Rows",
      description: "Select specific rows or ranges in your sheet and send messages in bulk with intelligent rate limiting and error handling."
    },
    {
      icon: <BarChart3 className="feature-icon-svg" />,
      title: "Status Logging to Sheet",
      description: "Real-time delivery status updates written back to your sheet with detailed success, error, and pending indicators."
    },
    {
      icon: <RefreshCw className="feature-icon-svg" />,
      title: "Auto-refresh Row Selection",
      description: "Dynamic row selection updates as you modify your sheet, ensuring you always send to the intended recipients."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Select rows",
      description: "Choose the rows in your Google Sheet containing the contacts you want to message. Select individual rows or entire ranges."
    },
    {
      number: "2",
      title: "Choose mode/template",
      description: "Pick between Meta-approved templates for business messaging or create custom messages with {ColumnName} placeholders."
    },
    {
      number: "3",
      title: "Validate preview",
      description: "Review your message preview with real data substitution. Check phone number validation and fix any issues before sending."
    },
    {
      number: "4",
      title: "Send and monitor statuses",
      description: "Send your messages with intelligent rate limiting. Monitor delivery status in real-time with updates written back to your sheet."
    }
  ];

  const pricingPlans = [
    {
      name: "Free",
      price: { monthly: 0, yearly: 0 },
      description: "Get started with basic messaging",
      features: [
        "50 messages (one-time)",
        "Meta templates",
        "Custom templates",
        "Status tracking",
        "Email support"
      ],
      cta: "Get Started",
      highlighted: false
    },
    {
      name: "Simple",
      price: { monthly: 5, yearly: 5 },
      description: "Perfect for small teams",
      features: [
        "1,000 messages/month",
        "Everything in Free",
        "Priority email support",
        "Advanced validation",
        "Bulk send support"
      ],
      cta: "Get Started",
      highlighted: false
    },
    {
      name: "Intermediate",
      price: { monthly: 29, yearly: 23 },
      description: "Ideal for growing businesses",
      features: [
        "6,000 messages/month",
        "Everything in Simple",
        "Advanced analytics",
        "Priority support",
        "Team collaboration"
      ],
      cta: "Get Started",
      highlighted: true
    },
    {
      name: "Ultimate",
      price: { monthly: 79, yearly: 63 },
      description: "For high-volume messaging needs",
      features: [
        "Unlimited messages",
        "Everything in Intermediate",
        "No rate limits*",
        "40,000/day bulk guardrail",
        "Dedicated support"
      ],
      cta: "Get Started",
      highlighted: false
    }
  ];

  const faqs = [
    {
      question: "Does this use the official WhatsApp Cloud API?",
      answer: "Yes, Sheets2WhatsApp integrates directly with Meta's official WhatsApp Cloud API, ensuring reliable delivery and compliance with WhatsApp's messaging policies."
    },
    {
      question: "Can I use Meta-approved templates?",
      answer: "Absolutely. Sheets2WhatsApp supports all Meta-approved message templates including marketing, utility, and authentication templates for business messaging."
    },
    {
      question: "How does personalization from sheet columns work?",
      answer: "Use {ColumnName} placeholders in your templates. The add-on automatically replaces these with actual values from your sheet columns for each recipient."
    },
    {
      question: "What happens when sending fails for some rows?",
      answer: "Failed messages are marked with detailed error codes in your sheet. Common issues include invalid phone numbers, blocked recipients, or API rate limits. You can retry failed messages after fixing issues."
    },
    {
      question: "Do you impose WhatsApp rate limits?",
      answer: "No. Sheets2WhatsApp does not set WhatsApp messaging rate limits on your account. Sending limits are controlled by Meta/WhatsApp and your own API/provider plan. If Meta or your provider throttles, delays, or blocks messages, those limits apply automatically."
    },
    {
      question: "Are there safeguards for bulk sending?",
      answer: "Yes, Sheets2WhatsApp includes intelligent rate limiting to prevent API violations, automatic retry logic for failed messages, and detailed error reporting. The Ultimate plan includes a bulk guardrail of 40,000 messages per day."
    }
  ];

  return (
    <div className="home-container">
      {/* Navigation */}
      <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
        <div className="container">
          <div className="nav-content">
            <div className="nav-brand">
              <div className="nav-logo">
                <div className="logo-icon">
                  <MessageSquare size={24} />
                </div>
                <span className="logo-text">Sheets2WhatsApp</span>
              </div>
            </div>
            
            <div className={`nav-links ${mobileMenuOpen ? 'nav-links-mobile' : ''}`}>
              <button onClick={() => scrollToSection('features')} className="nav-link">Features</button>
              <button onClick={() => scrollToSection('how-it-works')} className="nav-link">How It Works</button>
              <button onClick={() => scrollToSection('security')} className="nav-link">Security</button>
              <button onClick={() => scrollToSection('pricing')} className="nav-link">Pricing</button>
              <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
            </div>

            <div className="nav-actions">
              <button className="btn btn-primary">Install Add-on</button>
              <button 
                className="mobile-menu-toggle"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">Send WhatsApp messages directly from Google Sheets</h1>
              <p className="hero-subtitle">
                Professional WhatsApp messaging with official Cloud API support. Send personalized messages, 
                use Meta templates, and track delivery status—all from your spreadsheet.
              </p>
              <div className="hero-actions">
                <button className="btn btn-primary btn-large">Install Add-on</button>
                <button 
                  className="btn btn-secondary btn-large"
                  onClick={() => setShowVideoModal(true)}
                >
                  <Play size={20} />
                  Watch Demo
                </button>
              </div>
            </div>
            
            <div className="hero-mockup">
              <div className="mockup-container">
                <div className="mockup-header">
                  <div className="mockup-title">WhatsApp Lead Manager</div>
                  <div className="mockup-status">
                    <span className="status-dot status-success"></span>
                    <span>Connected</span>
                  </div>
                </div>
                <div className="mockup-content">
                  <div className="mockup-section">
                    <label className="mockup-label">Delivery Mode</label>
                    <div className="mockup-select">
                      <span>Custom Template (API Text)</span>
                      <ChevronDown size={16} />
                    </div>
                  </div>
                  <div className="mockup-section">
                    <label className="mockup-label">Selected Rows</label>
                    <div className="mockup-value">
                      <CheckCircle2 size={16} className="mockup-icon" />
                      Row 2 - +91
                    </div>
                  </div>
                  <div className="mockup-section">
                    <label className="mockup-label">Template Preview</label>
                    <div className="mockup-preview">
                      <div className="preview-message">
                        <span className="preview-text">hello_world</span>
                        <span className="badge badge-success">
                          <Check size={12} />
                          Valid
                        </span>
                      </div>
                    </div>
                  </div>
                  <button className="btn btn-primary btn-full mockup-btn">
                    <Send size={16} />
                    Send Messages
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <section className="trust-strip">
        <div className="container">
          <div className="trust-items">
            <div className="trust-item">
              <Star className="trust-icon" />
              <span>Works with Google Workspace</span>
            </div>
            <div className="trust-item">
              <CheckCircle2 className="trust-icon" />
              <span>Powered by WhatsApp Cloud API</span>
            </div>
            <div className="trust-item">
              <MessageSquare className="trust-icon" />
              <span>Supports Meta + Custom Templates</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" id="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Everything you need for professional WhatsApp messaging</h2>
            <p className="section-subtitle">
              Streamline your communication workflow with powerful features designed for reliability and control.
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works" id="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How it works</h2>
            <p className="section-subtitle">Get started with WhatsApp messaging in four simple steps</p>
          </div>
          <div className="steps-container">
            {steps.map((step, index) => (
              <div key={index} className="step">
                <div className="step-number">{step.number}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Preview Section */}
      <section className="interactive-preview">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">See validation in action</h2>
            <p className="section-subtitle">Watch how Sheets2WhatsApp handles different row scenarios</p>
          </div>
          <div className="preview-container">
            <div className="preview-sheet">
              <div className="sheet-header">
                <div className="sheet-cell">Name</div>
                <div className="sheet-cell">Phone</div>
                <div className="sheet-cell">Status</div>
              </div>
              <div className="sheet-row">
                <div className="sheet-cell">Sarah Jones</div>
                <div className="sheet-cell">+919019...</div>
                <div className="sheet-cell">
                  <span className="badge badge-success">
                    <Check size={12} />
                    Valid
                  </span>
                </div>
              </div>
              <div className="sheet-row">
                <div className="sheet-cell">John Smith</div>
                <div className="sheet-cell">-</div>
                <div className="sheet-cell">
                  <span className="badge badge-error">
                    <XCircle size={12} />
                    No Phone
                  </span>
                </div>
              </div>
              <div className="sheet-row">
                <div className="sheet-cell">Name</div>
                <div className="sheet-cell">Phone Number</div>
                <div className="sheet-cell">
                  <span className="badge badge-info">
                    <AlertCircle size={12} />
                    Header Row
                  </span>
                </div>
              </div>
              <div className="sheet-row">
                <div className="sheet-cell">Mike Chen</div>
                <div className="sheet-cell">9483...</div>
                <div className="sheet-cell">
                  <span className="badge badge-warning">
                    <AlertTriangle size={12} />
                    Needs Country Code
                  </span>
                </div>
              </div>
            </div>
            <div className="preview-alerts">
              <div className="alert alert-success">
                <div className="alert-icon">
                  <Check size={20} />
                </div>
                <div className="alert-content">
                  <strong>Valid numbers ready to send</strong>
                  <p>Phone numbers with proper country codes will be processed immediately.</p>
                </div>
              </div>
              <div className="alert alert-warning">
                <div className="alert-icon">
                  <AlertTriangle size={20} />
                </div>
                <div className="alert-content">
                  <strong>10-digit numbers detected</strong>
                  <p>Numbers without country codes will be prompted for region selection.</p>
                </div>
              </div>
              <div className="alert alert-error">
                <div className="alert-icon">
                  <XCircle size={20} />
                </div>
                <div className="alert-content">
                  <strong>Missing phone numbers</strong>
                  <p>Rows without valid phone numbers will be skipped automatically.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="security" id="security">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Security & Reliability</h2>
            <p className="section-subtitle">Built with enterprise-grade security and robust error handling</p>
          </div>
          <div className="security-grid">
            <div className="security-card">
              <div className="security-icon">
                <Shield size={32} />
              </div>
              <h3 className="security-title">No Unnecessary Data Retention</h3>
              <p className="security-description">
                We don't process any data during active messaging sessions. No contact information is stored 
                on our servers after sending.
              </p>
            </div>
            <div className="security-card">
              <div className="security-icon">
                <Zap size={32} />
              </div>
              <h3 className="security-title">Secure Credential Handling</h3>
              <p className="security-description">
                Your WhatsApp API credentials are encrypted and stored securely with Google's 
                enterprise-grade security infrastructure.
              </p>
            </div>
            <div className="security-card">
              <div className="security-icon">
                <AlertCircle size={32} />
              </div>
              <h3 className="security-title">Error Handling & Safe Bulk Sending</h3>
              <p className="security-description">
                Intelligent rate limiting, automatic retry logic, and detailed error reporting ensure 
                reliable message delivery without API violations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="pricing" id="pricing">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Simple, transparent pricing</h2>
            <p className="section-subtitle">Choose the plan that fits your messaging volume</p>
            <div className="billing-toggle">
              <button 
                className={`billing-option ${billingPeriod === 'monthly' ? 'active' : ''}`}
                onClick={() => setBillingPeriod('monthly')}
              >
                Monthly
              </button>
              <button 
                className={`billing-option ${billingPeriod === 'yearly' ? 'active' : ''}`}
                onClick={() => setBillingPeriod('yearly')}
              >
                Yearly <span className="billing-discount">Save 20%</span>
              </button>
            </div>
          </div>
          <div className="pricing-grid">
            {pricingPlans.map((plan, index) => (
              <div 
                key={index} 
                className={`pricing-card ${plan.highlighted ? 'pricing-card-featured' : ''}`}
              >
                {plan.highlighted && <div className="pricing-badge">Most Popular</div>}
                <div className="pricing-header">
                  <h3 className="plan-name">{plan.name}</h3>
                  <div className="plan-price">
                    <span className="price-amount">
                      ${billingPeriod === 'monthly' ? plan.price.monthly : plan.price.yearly}
                    </span>
                    {plan.price.monthly > 0 && <span className="price-period">/month</span>}
                  </div>
                  <p className="plan-description">{plan.description}</p>
                </div>
                <div className="pricing-features">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="feature-item">
                      <Check className="feature-check" size={16} />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
                <button className={`btn ${plan.highlighted ? 'btn-primary' : 'btn-outline'} btn-full`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
          <div className="pricing-note">
            <p>
              * WhatsApp delivery speed and throughput depend on Meta/WhatsApp Cloud API policies, 
              your WhatsApp Business account quality, and your provider plan limits. 
              Sheets2WhatsApp does not set rate limits on your account.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Frequently Asked Questions</h2>
            <p className="section-subtitle">Get answers to common questions about Sheets2WhatsApp</p>
          </div>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={index} className={`faq-item ${openFaq === index ? 'faq-item-open' : ''}`}>
                <button 
                  className="faq-question"
                  onClick={() => toggleFaq(index)}
                >
                  <h3>{faq.question}</h3>
                  <ChevronDown className="faq-toggle" />
                </button>
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <div className="footer-brand">
                <MessageSquare size={24} />
                <span>Sheets2WhatsApp</span>
              </div>
              <p className="footer-description">
                Professional WhatsApp messaging directly from Google Sheets
              </p>
            </div>
            <div className="footer-section">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="footer-section">
              <h4>Legal</h4>
              <Link to="/terms">Terms of Service</Link>
              <Link to="/terms#privacy">Privacy Policy</Link>
              <Link to="/terms#refund">Refund Policy</Link>
            </div>
            <div className="footer-section">
              <h4>Contact</h4>
              <a href="mailto:contact@projectdns.in">contact@projectdns.in</a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2026 Sheets2WhatsApp. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      {showVideoModal && (
        <div className="video-modal" onClick={() => setShowVideoModal(false)}>
          <div className="video-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="video-modal-close" onClick={() => setShowVideoModal(false)}>
              <X size={24} />
            </button>
            <div className="video-container">
              <iframe
                width="100%"
                height="100%"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Sheets2WhatsApp Demo"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
